import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  Alert,
  ActivityIndicator,
  RefreshControl,
  Modal,
  TextInput,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';
import { useSettlementStore } from '../../stores/settlementStore';
import { SettlementRecord } from '../../types';

const { width: screenWidth } = Dimensions.get('window');
const isTablet = screenWidth > 768;
const isWeb = Platform.OS === 'web';

interface PaymentHistoryScreenProps {
  navigation?: any;
}

interface PaymentRecord {
  id: string;
  settlement_id: string;
  driver_id: string;
  driver_name: string;
  vehicle_plate: string;
  settlement_period: string;
  amount_paid: number;
  payment_date: string;
  payment_method: string;
  payment_reference?: string;
  notes?: string;
  status: string;
}

export const PaymentHistoryScreen: React.FC<PaymentHistoryScreenProps> = ({ navigation }) => {
  const { settlements, loading: settlementsLoading, fetchSettlements } = useSettlementStore();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [paymentRecords, setPaymentRecords] = useState<PaymentRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<PaymentRecord[]>([]);
  
  // Filter states
  const [searchText, setSearchText] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showFilterModal, setShowFilterModal] = useState(false);
  
  // Detail modal
  const [selectedPayment, setSelectedPayment] = useState<PaymentRecord | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalPaid: 0,
    totalOutstanding: 0,
    thisMonthPaid: 0,
    totalPayments: 0,
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const RECORDS_PER_PAGE = 30;

  useEffect(() => {
    loadPaymentHistory();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (settlements) {
      processPaymentRecords();
    }
  }, [settlements]);

  useEffect(() => {
    applyFilters();
  }, [searchText, selectedPaymentMethod, paymentRecords]);

  const loadPaymentHistory = async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Fetch settlements for the selected period
      await fetchSettlements({
        year: selectedYear,
        month: selectedMonth,
      });

    } catch (error) {
      console.error('❌ Error loading payment history:', error);
      Alert.alert('Error', 'Failed to load payment history');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const processPaymentRecords = () => {
    const payments: PaymentRecord[] = [];
    let totalPaid = 0;
    let totalOutstanding = 0;
    let thisMonthPaid = 0;
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    settlements.forEach(settlement => {
      // Only process paid settlements or those with partial payments
      if (settlement.status === 'paid' || (settlement.total_paid_amount && settlement.total_paid_amount > 0)) {
        const paidAmount = settlement.total_paid_amount || 0;
        const outstandingAmount = settlement.outstanding_balance || 0;

        totalPaid += paidAmount;
        totalOutstanding += outstandingAmount;

        if (settlement.period_year === currentYear && settlement.period_month === currentMonth) {
          thisMonthPaid += paidAmount;
        }

        // Create payment record
        payments.push({
          id: settlement.id,
          settlement_id: settlement.id,
          driver_id: settlement.driver_id,
          driver_name: settlement.driver?.name || 'Unknown Driver',
          vehicle_plate: settlement.vehicle?.license_plate || 'N/A',
          settlement_period: `${settlement.period_month}/${settlement.period_year}`,
          amount_paid: paidAmount,
          payment_date: settlement.paid_date || settlement.generated_date,
          payment_method: settlement.payment_method || 'N/A',
          payment_reference: settlement.payment_reference,
          notes: settlement.notes,
          status: settlement.status,
        });
      }
    });

    setPaymentRecords(payments);
    setSummaryStats({
      totalPaid,
      totalOutstanding,
      thisMonthPaid,
      totalPayments: payments.length,
    });
  };

  const applyFilters = () => {
    let filtered = [...paymentRecords];

    // Search filter
    if (searchText) {
      filtered = filtered.filter(record =>
        record.driver_name.toLowerCase().includes(searchText.toLowerCase()) ||
        record.vehicle_plate.toLowerCase().includes(searchText.toLowerCase()) ||
        record.payment_reference?.toLowerCase().includes(searchText.toLowerCase()) ||
        record.settlement_period.includes(searchText)
      );
    }

    // Payment method filter
    if (selectedPaymentMethod !== 'all') {
      filtered = filtered.filter(record => record.payment_method === selectedPaymentMethod);
    }

    setFilteredRecords(filtered);
  };

  const onRefresh = () => {
    loadPaymentHistory(true);
  };

  const formatCurrency = (amount: number) => {
    return `AED ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case 'cash': return 'cash';
      case 'bank_transfer': return 'card';
      case 'check': return 'document';
      default: return 'wallet';
    }
  };

  const getPaymentMethodColor = (method: string) => {
    switch (method.toLowerCase()) {
      case 'cash': return '#10B981';
      case 'bank_transfer': return '#3B82F6';
      case 'check': return '#F59E0B';
      default: return '#6B7280';
    }
  };

  const renderSummaryCards = () => (
    <View style={styles.summarySection}>
      <View style={styles.summaryGrid}>
        {/* Total Paid */}
        <View style={[styles.summaryCard, { borderLeftColor: '#10B981' }]}>
          <Ionicons name="checkmark-circle" size={24} color="#10B981" />
          <View style={styles.summaryContent}>
            <Text style={styles.summaryLabel}>Total Paid</Text>
            <Text style={[styles.summaryValue, { color: '#10B981' }]}>
              {formatCurrency(summaryStats.totalPaid)}
            </Text>
          </View>
        </View>

        {/* Outstanding */}
        <View style={[styles.summaryCard, { borderLeftColor: '#EF4444' }]}>
          <Ionicons name="alert-circle" size={24} color="#EF4444" />
          <View style={styles.summaryContent}>
            <Text style={styles.summaryLabel}>Outstanding</Text>
            <Text style={[styles.summaryValue, { color: '#EF4444' }]}>
              {formatCurrency(summaryStats.totalOutstanding)}
            </Text>
          </View>
        </View>

        {/* This Month */}
        <View style={[styles.summaryCard, { borderLeftColor: '#3B82F6' }]}>
          <Ionicons name="calendar" size={24} color="#3B82F6" />
          <View style={styles.summaryContent}>
            <Text style={styles.summaryLabel}>This Month</Text>
            <Text style={[styles.summaryValue, { color: '#3B82F6' }]}>
              {formatCurrency(summaryStats.thisMonthPaid)}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );

  const renderFilterBar = () => (
    <View style={styles.filterBar}>
      {/* Search */}
      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color={theme.colors.gray400} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search by driver, vehicle, reference..."
          value={searchText}
          onChangeText={setSearchText}
        />
        {searchText ? (
          <TouchableOpacity onPress={() => setSearchText('')}>
            <Ionicons name="close-circle" size={20} color={theme.colors.gray400} />
          </TouchableOpacity>
        ) : null}
      </View>

      {/* Filter Button */}
      <TouchableOpacity
        style={styles.filterButton}
        onPress={() => setShowFilterModal(true)}
      >
        <Ionicons name="filter" size={20} color={theme.colors.primary} />
        <Text style={styles.filterButtonText}>Filters</Text>
      </TouchableOpacity>
    </View>
  );

  const renderPaymentTable = () => {
    const paginatedRecords = filteredRecords.slice(0, currentPage * RECORDS_PER_PAGE);

    if (paginatedRecords.length === 0) {
      return (
        <View style={styles.emptyState}>
          <Ionicons name="receipt-outline" size={64} color={theme.colors.gray400} />
          <Text style={styles.emptyStateTitle}>No Payment Records</Text>
          <Text style={styles.emptyStateDescription}>
            No payment records found for the selected period.
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.tableContainer}>
        {paginatedRecords.map((record) => (
          <TouchableOpacity
            key={record.id}
            style={styles.paymentCard}
            onPress={() => {
              setSelectedPayment(record);
              setShowDetailModal(true);
            }}
          >
            <View style={styles.paymentCardHeader}>
              <View style={styles.paymentCardLeft}>
                <View style={[
                  styles.paymentMethodIcon,
                  { backgroundColor: getPaymentMethodColor(record.payment_method) + '20' }
                ]}>
                  <Ionicons
                    name={getPaymentMethodIcon(record.payment_method) as any}
                    size={20}
                    color={getPaymentMethodColor(record.payment_method)}
                  />
                </View>
                <View style={styles.paymentCardInfo}>
                  <Text style={styles.paymentCardDriver}>{record.driver_name}</Text>
                  <Text style={styles.paymentCardVehicle}>{record.vehicle_plate}</Text>
                </View>
              </View>
              <View style={styles.paymentCardRight}>
                <Text style={[styles.paymentCardAmount, { color: '#10B981' }]}>
                  {formatCurrency(record.amount_paid)}
                </Text>
                <Text style={styles.paymentCardDate}>{formatDate(record.payment_date)}</Text>
              </View>
            </View>

            <View style={styles.paymentCardFooter}>
              <View style={styles.paymentCardDetail}>
                <Text style={styles.paymentCardDetailLabel}>Period:</Text>
                <Text style={styles.paymentCardDetailValue}>{record.settlement_period}</Text>
              </View>
              <View style={styles.paymentCardDetail}>
                <Text style={styles.paymentCardDetailLabel}>Method:</Text>
                <Text style={styles.paymentCardDetailValue}>
                  {record.payment_method.replace('_', ' ').toUpperCase()}
                </Text>
              </View>
              {record.payment_reference && (
                <View style={styles.paymentCardDetail}>
                  <Text style={styles.paymentCardDetailLabel}>Ref:</Text>
                  <Text style={styles.paymentCardDetailValue} numberOfLines={1}>
                    {record.payment_reference}
                  </Text>
                </View>
              )}
            </View>
          </TouchableOpacity>
        ))}

        {/* Load More Button */}
        {filteredRecords.length > currentPage * RECORDS_PER_PAGE && (
          <TouchableOpacity
            style={styles.loadMoreButton}
            onPress={() => setCurrentPage(prev => prev + 1)}
          >
            <Ionicons name="add-circle-outline" size={20} color={theme.colors.primary} />
            <Text style={styles.loadMoreText}>Load More</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderDetailModal = () => (
    <Modal
      visible={showDetailModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowDetailModal(false)}
    >
      <View style={styles.modalContainer}>
        {/* Header */}
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Payment Details</Text>
          <TouchableOpacity
            onPress={() => setShowDetailModal(false)}
            style={styles.modalCloseButton}
          >
            <Ionicons name="close" size={24} color={theme.colors.gray600} />
          </TouchableOpacity>
        </View>

        {/* Content */}
        {selectedPayment && (
          <ScrollView style={styles.modalContent}>
            <View style={styles.detailSection}>
              <Text style={styles.detailSectionTitle}>Driver Information</Text>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Driver Name:</Text>
                <Text style={styles.detailValue}>{selectedPayment.driver_name}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Vehicle:</Text>
                <Text style={styles.detailValue}>{selectedPayment.vehicle_plate}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Period:</Text>
                <Text style={styles.detailValue}>{selectedPayment.settlement_period}</Text>
              </View>
            </View>

            <View style={styles.detailSection}>
              <Text style={styles.detailSectionTitle}>Payment Information</Text>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Amount Paid:</Text>
                <Text style={[styles.detailValue, { color: '#10B981', fontWeight: '600' }]}>
                  {formatCurrency(selectedPayment.amount_paid)}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Payment Date:</Text>
                <Text style={styles.detailValue}>{formatDate(selectedPayment.payment_date)}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Payment Method:</Text>
                <Text style={styles.detailValue}>
                  {selectedPayment.payment_method.replace('_', ' ').toUpperCase()}
                </Text>
              </View>
              {selectedPayment.payment_reference && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Reference:</Text>
                  <Text style={styles.detailValue}>{selectedPayment.payment_reference}</Text>
                </View>
              )}
              {selectedPayment.notes && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Notes:</Text>
                  <Text style={styles.detailValue}>{selectedPayment.notes}</Text>
                </View>
              )}
            </View>

            <TouchableOpacity
              style={styles.viewSettlementButton}
              onPress={() => {
                setShowDetailModal(false);
                navigation?.navigate('SettlementDetail', {
                  settlementId: selectedPayment.settlement_id
                });
              }}
            >
              <Ionicons name="document-text" size={20} color={theme.colors.white} />
              <Text style={styles.viewSettlementButtonText}>View Full Settlement</Text>
            </TouchableOpacity>
          </ScrollView>
        )}
      </View>
    </Modal>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading payment history...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation?.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Payment History</Text>
        <View style={{ width: 24 }} />
      </View>

      {/* Content */}
      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {renderSummaryCards()}
        {renderFilterBar()}
        <View style={styles.resultSection}>
          <Text style={styles.resultCount}>
            {filteredRecords.length} payment{filteredRecords.length !== 1 ? 's' : ''} found
          </Text>
        </View>
        {renderPaymentTable()}
      </ScrollView>

      {renderDetailModal()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  content: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  headerTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.gray50,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.gray600,
  },

  // Summary Section
  summarySection: {
    padding: theme.spacing.md,
  },
  summaryGrid: {
    gap: theme.spacing.md,
  },
  summaryCard: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    alignItems: 'center',
    gap: theme.spacing.md,
  },
  summaryContent: {
    flex: 1,
  },
  summaryLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
    marginBottom: 4,
  },
  summaryValue: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as any,
  },

  // Filter Bar
  filterBar: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    paddingBottom: theme.spacing.md,
  },
  searchContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    gap: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
  },
  searchInput: {
    flex: 1,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textPrimary,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    backgroundColor: theme.colors.white,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.primary,
  },
  filterButtonText: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    color: theme.colors.primary,
  },

  // Result Section
  resultSection: {
    paddingHorizontal: theme.spacing.md,
    paddingBottom: theme.spacing.sm,
  },
  resultCount: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
  },

  // Table
  tableContainer: {
    padding: theme.spacing.md,
    gap: theme.spacing.md,
  },
  paymentCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  paymentCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
  },
  paymentCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    flex: 1,
  },
  paymentMethodIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paymentCardInfo: {
    flex: 1,
  },
  paymentCardDriver: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  paymentCardVehicle: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
    marginTop: 2,
  },
  paymentCardRight: {
    alignItems: 'flex-end',
  },
  paymentCardAmount: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '700' as any,
  },
  paymentCardDate: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
    marginTop: 2,
  },
  paymentCardFooter: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.md,
    paddingTop: theme.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray100,
  },
  paymentCardDetail: {
    flexDirection: 'row',
    gap: 4,
  },
  paymentCardDetailLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
  },
  paymentCardDetailValue: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textPrimary,
    fontWeight: '500' as any,
  },

  // Empty State
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xl,
  },
  emptyStateTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginTop: theme.spacing.md,
  },
  emptyStateDescription: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.gray600,
    textAlign: 'center',
    marginTop: theme.spacing.sm,
  },

  // Load More
  loadMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xs,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.colors.primary + '10',
    borderRadius: theme.borderRadius.md,
  },
  loadMoreText: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    color: theme.colors.primary,
  },

  // Detail Modal
  modalContainer: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  modalTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  modalCloseButton: {
    padding: theme.spacing.sm,
  },
  modalContent: {
    flex: 1,
    padding: theme.spacing.lg,
  },
  detailSection: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  detailSectionTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  detailLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
  },
  detailValue: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textPrimary,
    fontWeight: '500' as any,
    textAlign: 'right',
    flex: 1,
    marginLeft: theme.spacing.md,
  },
  viewSettlementButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.primary,
    paddingVertical: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.md,
  },
  viewSettlementButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
});

export default PaymentHistoryScreen;
